/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2020 - 2020
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useState } from "react";
import axios from "axios";

import Autocomplete from "@material-ui/lab/Autocomplete";
import { useTheme } from "@material-ui/core/styles";
import styled from "styled-components";

import StyledTextField from "./StyledTextField";

import { getLocations } from "../../api/api";
import types from "../../helpers/types";

const StyledAutoComplete = styled(({ theme, ...props }) => (
  <Autocomplete {...props} />
))`
  .MuiAutocomplete-input {
    color: ${(props) => props.theme.palette.primary.dark};
  }

  .MuiAutocomplete-popupIndicator {
    display: none;
  }
`;

const locationsCache = {};
let cancelTokenSource;

const LocationAutoComplete = ({
  autocompleteTestId = "",
  error,
  handleChange,
  label = "Location",
  location,
  width,
}) => {
  const theme = useTheme();
  const [locationsRequestStatus, setLocationsRequestStatus] = useState(
    types.requestStatuses.initial
  );
  const [locations, setLocations] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setLocations([]);
  };

  const handleInputChange = ({ target: { value } }) => {
    setInputValue(value);

    if (value.length > 2) {
      fetchLocations(value);
    } else if (value.length < 3 && locations.length) {
      setLocations([]);
    }
  };

  const fetchLocations = async (searchTerm) => {
    const cachedLocations = locationsCache[searchTerm];
    if (cachedLocations) {
      setLocations(cachedLocations);
      return;
    }

    if (cancelTokenSource) {
      cancelTokenSource.cancel();
    }

    cancelTokenSource = axios.CancelToken.source();
    setLocationsRequestStatus(types.requestStatuses.inProgress);

    const locations = await getLocations(searchTerm, cancelTokenSource.token);

    locationsCache[searchTerm] = locations;
    setLocationsRequestStatus(types.requestStatuses.success);
    setLocations(locations);
  };

  const filterOptions = (options) =>
    options.filter((option) => option.length <= 256);

  return (
    <StyledAutoComplete
      data-testid={autocompleteTestId}
      filterOptions={filterOptions}
      loading={
        open && locationsRequestStatus === types.requestStatuses.inProgress
      }
      onOpen={handleOpen}
      onChange={handleChange}
      onClose={handleClose}
      open={open}
      options={locations}
      noOptionsText={
        inputValue.length > 2
          ? "No results"
          : "Please enter at least 3 characters"
      }
      style={width ? { width } : {}}
      renderInput={(params) => (
        <StyledTextField
          {...params}
          label={label}
          error={error}
          variant="outlined"
          onChange={handleInputChange}
        />
      )}
      theme={theme}
      value={location?.length ? location : null}
    />
  );
};

export default LocationAutoComplete;
